import { Component, Input, OnInit } from '@angular/core';

// Model
import { STATES } from '../../model/states';
import { SchoolInfo } from '../../model/free-trial';

// Services
import { FreeTrialService } from '../../services/free-trial.service';
import { DataService } from '../../services/data.service';

// RXJS
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

// AFS
import { AngularFirestore } from '@angular/fire/firestore';

@Component({
	selector: 'app-register-school',
	templateUrl: './register-school.component.html',
})
export class RegisterSchoolComponent implements OnInit {

	private doneClicked: boolean = false;

	schoolInfo: SchoolInfo = new SchoolInfo('','','','','','','');

	schoolNameOrCityChanged: Subject<any> = new Subject();

	takenSchoolName: boolean;

	isGeneratingAlias: boolean = false;
	hasGeneratedAliases: boolean = false;

	// Optional Parameter. 
	// If passed in, binds to the existing school info
	@Input() existingSchoolInfo?: SchoolInfo;
	constructor(
		private freeTrialSvc: FreeTrialService,
		private dataSvc: DataService,
		private afs: AngularFirestore
	) { }

	ngOnInit() {
		if (!!this.existingSchoolInfo)
			this.schoolInfo = this.existingSchoolInfo;

		// On school name change or city change, generate alias
		this.schoolNameOrCityChanged.pipe(debounceTime(600)).subscribe(() => {
			this.checkAndGenerateSchoolAliases();
		});
	}

	get states() { return STATES; }

	get schoolURL() { return this.schoolInfo.getURL(); }

	get generatedAliases() { return this.schoolInfo.generatedAliases; }

	async btnDone(): Promise<any> {
		this.doneClicked = true;

		/*
			Return If:
				- Any Required Fields are Empty
				- Still Generating Alias
				- School Name is taken
				- checking school name
		*/
		if (this.isInvalidFormInput(this.schoolInfo.schoolName) || 
			this.isInvalidFormInput(this.schoolInfo.phoneNumber) || 
			this.isInvalidFormInput(this.schoolInfo.alias) || 
			this.isInvalidFormInput(this.schoolInfo.address) ||
			this.isInvalidFormInput(this.schoolInfo.city) || 
			this.isInvalidFormInput(this.schoolInfo.state) ||
			this.isInvalidFormInput(this.schoolInfo.zip) ||
			this.isGeneratingAlias ||
			this.takenSchoolName
		) {
			return;
		}

		try {
			let trimmedName = this.schoolInfo.schoolName.trim();

			// Check Alias and School Name for duplicates
			// Test phone number
			let [existing, phoneRes] = await Promise.all([
				this.checkForExistingSchool(trimmedName),
				this.dataSvc.lookupPhone(this.schoolInfo.phoneNumber)
			]);

			if (!!existing || existing === undefined) {
				return;
			}

			// If err or empty phone property, invalid phone number, or no carrier (meaning it's fake)
			if (!phoneRes || phoneRes.err || phoneRes.carrier.error_code) {
				this.afs.collection('logging').add({
					type: "invalid-phone", phone: this.schoolInfo.phoneNumber, response: phoneRes, schoolName: trimmedName
				})
				throw new Error('Invalid phone number.')
			}

			this.schoolInfo.schoolName = trimmedName;
			return this.schoolInfo;
		}
		catch(e) {
			return Promise.reject(e);
		}
	}

	// Check /daycares and /potentialDaycares for existing daycares with the same name/alias
    async checkForExistingSchool(schoolName): Promise<boolean> {
		this.takenSchoolName = false;

		let promises = [
			this.checkExistingSchoolName(schoolName),
		];

		try {
			let responses = await Promise.all(promises);

			if ((responses[0]))
				this.takenSchoolName = true;

			return this.takenSchoolName
		}
		catch(e) {
			return Promise.reject(e);
		}
	}
	
	// Check /daycares and /potentialDaycares to see if the school name exists already
	// Return True if it exists
	async checkExistingSchoolName(schoolName: string): Promise<boolean> {
		let promises = [
			this.freeTrialSvc.checkForSchoolByName(schoolName),
			this.freeTrialSvc.checkForSchoolByName(schoolName,true)
		];

		let res = await Promise.all(promises);

		if ((res[0] && res[0].result) || (res[1] && res[1].result)) 
			return true;

		return false;
	}

	async checkAndGenerateSchoolAliases(): Promise<any> {
		if (this.isGeneratingAlias || !this.schoolInfo.schoolName || !this.schoolInfo.city) {
			return;
		}

		this.isGeneratingAlias = true;
		this.hasGeneratedAliases = false;

		try {
			let aliases = [];

			for (let i of [1,2,3,4,5,6]) {
				// Generate alias based of tryNumber param
				let newAliasOption = this.schoolInfo.generateAlias(i);

				// Check if alias exists, daycare names with 1 word only have two alias options
				if (newAliasOption) {
					aliases.push(newAliasOption);
				}
			}

			let aliasResponses = await this.freeTrialSvc.checkForSchoolByAlias(aliases)

			let validAliases = [];
			// loop through returned promises by 2's
			for (let index = 0; index < aliasResponses.length; index+=2) {
				const daycaresAlias = aliasResponses[index];
				const potentialAlias = aliasResponses[index+1];
				
				// Check that the alias is unused before pushing
				if (!daycaresAlias && !potentialAlias) {
					validAliases.push(aliases[index / 2])
				}
			}

			if (validAliases.length < 3)
				this.schoolInfo.generatedAliases = validAliases;
			
			this.schoolInfo.generatedAliases = validAliases.slice(0,3);

			// Automatically select an alias
			this.schoolInfo.alias = validAliases[0] || ''

		} catch (e) {
			console.log(e);
		}

		this.isGeneratingAlias = false;
		this.hasGeneratedAliases = true;

		return;
	}

	// Used Form Validation
	isInvalidFormInput(propertyValue: any, touched?: boolean): boolean {
		if (!touched && !this.doneClicked)
			return false; 
		if (!!propertyValue)
			return false

		return true;
	}

	/*
		Functions used for selecting alias
	*/

	activate(alias: string) {
		this.schoolInfo.alias = alias; 
	}

	getActiveClass(alias: string) {
		if (this.isChecked(alias)) {
			'chosen'
		}
		return ''
	}

	isChecked(alias: string) {
		return this.schoolInfo.alias === alias;
	}
}
